import { createFileRoute } from '@tanstack/react-router';
import { useState, type ReactElement } from 'react';
import { useViewport } from '@/lib/hooks/use-viewport';
import { DAdminShell } from '@/components/organisms/admin-shell';
import { AdminDesktopOnlyNotice } from '@/components/organisms/admin-desktop-only-notice';
import { ModerationQueuePane } from '@/components/organisms/moderation-queue-pane';
import { useModerationCandidates } from '@/lib/queries/moderation';

const PAGE_SIZE = 20;
const COMING_SOON_HINT = 'Coming soon';

const HEADER_BUTTON_CLASS =
  'h-8 px-3 rounded-[var(--r-sm)] text-[12px] font-medium text-[var(--ink-4)] cursor-not-allowed';
const DECISION_BUTTON_CLASS =
  'h-9 px-3 rounded-[var(--r-sm)] text-[13px] font-medium text-[var(--ink-4)] bg-[var(--bg-soft)] cursor-not-allowed';

function QueueHeaderActions(): ReactElement {
  return (
    <>
      <button
        type="button"
        aria-disabled="true"
        title={COMING_SOON_HINT}
        className={HEADER_BUTTON_CLASS}
      >
        Filter
      </button>
      <button
        type="button"
        aria-disabled="true"
        title={COMING_SOON_HINT}
        className={HEADER_BUTTON_CLASS}
      >
        View as table
      </button>
    </>
  );
}

function QueueCentrePlaceholder(): ReactElement {
  return (
    <div className="flex-1 flex items-center justify-center px-8 py-12 text-center">
      <p className="text-[13px] text-[var(--ink-3)] max-w-[320px]">
        Select a submission from the queue to begin reviewing.
      </p>
    </div>
  );
}

function QueueDecisionPanelDisabled(): ReactElement {
  return (
    <aside className="border-l border-[var(--rule)] p-4 flex flex-col gap-3 bg-[var(--bg-surface)]">
      <div className="flex flex-col gap-2">
        {(['Approve', 'Merge', 'Edit then approve', 'Reject'] as const).map((label) => (
          <button
            key={label}
            type="button"
            aria-disabled="true"
            title={COMING_SOON_HINT}
            className={DECISION_BUTTON_CLASS}
          >
            {label}
          </button>
        ))}
      </div>
      <textarea
        aria-label="Reason"
        placeholder="Reason (required for reject)"
        disabled
        className="resize-none h-24 p-2 text-[12px] rounded-[var(--r-sm)] border border-[var(--rule)] bg-[var(--bg-soft)] text-[var(--ink-4)]"
      />
    </aside>
  );
}

function AdminQueuePage(): ReactElement {
  const viewport = useViewport();
  const [page, setPage] = useState(0);
  if (viewport !== 'desktop') return <AdminDesktopOnlyNotice />;
  const { data, isLoading } = useModerationCandidates({ page, size: PAGE_SIZE });
  const subtitle = data ? `${data.totalElements.toLocaleString()} pending` : '…';
  return (
    <DAdminShell
      section="queue"
      title="Moderation queue"
      subtitle={subtitle}
      actions={<QueueHeaderActions />}
    >
      <div className="grid grid-cols-[280px_1fr_320px] h-full">
        <ModerationQueuePane
          page={data}
          isLoading={isLoading}
          currentPage={page}
          onPageChange={setPage}
        />
        <QueueCentrePlaceholder />
        <QueueDecisionPanelDisabled />
      </div>
    </DAdminShell>
  );
}

export const Route = createFileRoute('/admin/queue/')({
  component: AdminQueuePage,
});
